import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { ConcessionComment, ConcessionMentionOptions } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface CommentsTabProps {
  comments: ConcessionComment[];
  newComment: string;
  isInternalComment: boolean;
  commentLoading: boolean;
  isCustomer: boolean;
  /** CR-6510: colleagues and departments the current user may ask to look. */
  mentionOptions: ConcessionMentionOptions | null;
  mentionedUserIds: number[];
  mentionedDepartments: string[];
  onCommentChange: (value: string) => void;
  onInternalChange: (value: boolean) => void;
  onMentionedUsersChange: (ids: number[]) => void;
  onMentionedDepartmentsChange: (departments: string[]) => void;
  onAddComment: () => void;
}

export function CommentsTab({
  comments,
  newComment,
  isInternalComment,
  commentLoading,
  isCustomer,
  mentionOptions,
  mentionedUserIds,
  mentionedDepartments,
  onCommentChange,
  onInternalChange,
  onMentionedUsersChange,
  onMentionedDepartmentsChange,
  onAddComment,
}: CommentsTabProps) {
  const { formatDateTime } = useFormatters();

  const toggleUser = (id: number) => {
    onMentionedUsersChange(
      mentionedUserIds.includes(id)
        ? mentionedUserIds.filter((existing) => existing !== id)
        : [...mentionedUserIds, id],
    );
  };

  const toggleDepartment = (department: string) => {
    onMentionedDepartmentsChange(
      mentionedDepartments.includes(department)
        ? mentionedDepartments.filter((existing) => existing !== department)
        : [...mentionedDepartments, department],
    );
  };

  // Whether there is anyone to ask — a question about the available options,
  // never about what is currently selected.
  const hasMentionTargets = !!(
    mentionOptions
    && (mentionOptions.users.length || mentionOptions.departments.length)
  );

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        {/* Add Comment Form */}
        <div className="space-y-3">
          <Textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => onCommentChange(e.target.value)}
            rows={3}
          />
          {/* CR-6510: ask a colleague to look. Offered only on an internal
              note, and only ever populated with people at the commenter's
              own company — naming someone across the relationship would
              notify them about a note they cannot open. */}
          {isInternalComment && hasMentionTargets && (
            <div className="rounded-lg border p-3 space-y-3">
              <div>
                <p className="text-sm font-medium">Ask someone to look</p>
                <p className="text-xs text-muted-foreground">
                  They are notified and the request is recorded on the thread.
                  Colleagues at your own organisation only.
                </p>
              </div>

              {!!mentionOptions?.departments.length && (
                <div className="flex flex-wrap gap-2">
                  {mentionOptions.departments.map((department) => (
                    <button
                      key={department}
                      type="button"
                      onClick={() => toggleDepartment(department)}
                      aria-pressed={mentionedDepartments.includes(department)}
                      className={`px-2 py-1 rounded-full text-xs border ${
                        mentionedDepartments.includes(department)
                          ? 'bg-blue-100 text-blue-800 border-blue-300'
                          : 'bg-muted/50 border-transparent hover:border-muted-foreground/30'
                      }`}
                    >
                      {department}
                    </button>
                  ))}
                </div>
              )}

              {!!mentionOptions?.users.length && (
                <div className="flex flex-wrap gap-2">
                  {mentionOptions.users.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => toggleUser(user.id)}
                      aria-pressed={mentionedUserIds.includes(user.id)}
                      className={`px-2 py-1 rounded-full text-xs border ${
                        mentionedUserIds.includes(user.id)
                          ? 'bg-blue-100 text-blue-800 border-blue-300'
                          : 'bg-muted/50 border-transparent hover:border-muted-foreground/30'
                      }`}
                      title={user.department || undefined}
                    >
                      {user.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            {isCustomer && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isInternalComment}
                  onChange={(e) => onInternalChange(e.target.checked)}
                />
                Internal comment (not visible to supplier)
              </label>
            )}
            <Button
              size="sm"
              onClick={onAddComment}
              disabled={commentLoading || !newComment.trim()}
            >
              {commentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Comment'}
            </Button>
          </div>
        </div>

        {/* Comments List */}
        <div className="space-y-4 border-t pt-4">
          {comments.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No comments yet</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className={`p-3 rounded-lg ${comment.is_internal ? 'bg-yellow-50 border border-yellow-200' : 'bg-muted/50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">{comment.created_by_name || 'Unknown'}</span>
                  <div className="flex items-center gap-2">
                    {comment.is_internal && (
                      <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300 text-xs">
                        Internal
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{formatDateTime(comment.created_at)}</span>
                  </div>
                </div>
                <p className="text-sm whitespace-pre-wrap">{comment.comment}</p>
                {/* CR-6510: who was asked to look, so the ask stays part of
                    the record rather than living in the notification. */}
                {!!(comment.mentioned_user_names?.length
                    || comment.mentioned_departments?.length) && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Asked to look:{' '}
                    {[...(comment.mentioned_user_names || []),
                      ...(comment.mentioned_departments || [])].join(', ')}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
